import { isValidCliCallbackPort } from '@sdk/sdk';

// RFC 7636 §4.2: a code challenge is 43–128 characters of the unreserved set.
const CODE_CHALLENGE_PATTERN = /^[A-Za-z0-9\-._~]{43,128}$/;
export const MCP_RETURN = 'mcp';
// Control characters and the bidi overrides/isolates that can make a name
// render as something else. Not all of \p{Cf}: that would also break emoji
// joined with ZWJ.
const UNSAFE_NAME_CHARACTERS = /[\p{Cc}\u202A-\u202E\u2066-\u2069]/gu;
const MAX_NAME_LENGTH = 64;

/**
 * What an assistant asks for when it opens the /cli page.
 *
 * A CLI names its device. Without a port it cannot listen locally (a remote
 * shell, a sandbox), so the code is shown for the user to paste instead of
 * being sent back.
 *
 * An MCP client (`return=mcp`) is named by the Sytex MCP server, never by the
 * link (anyone can rewrite a link's `name`), so its request starts unnamed and
 * takes the name the server sealed through `withName`. Its code goes back to
 * the server's fixed callback, never to an address from the link, and its
 * connection is bound to the one organization the user picks.
 */
export class CliAuthorizationRequest {
  readonly name: string;
  readonly codeChallenge: string;
  readonly state: string;
  readonly port: string | null;
  readonly isMcp: boolean;

  constructor({ name, codeChallenge, state, port, isMcp }: CliAuthorizationRequestData) {
    this.name = name;
    this.codeChallenge = codeChallenge;
    this.state = state;
    this.port = port;
    this.isMcp = isMcp;
  }

  /**
   * Reads the page's query, or returns null when the link is not one a CLI or
   * the MCP server would have built. The state is mandatory whenever the code
   * is sent back rather than shown: it is what lets the receiver match the
   * callback to the login it started.
   */
  static fromQuery(get: (key: string) => string | null): CliAuthorizationRequest | null {
    const codeChallenge = get('code_challenge') ?? '';
    const state = get('state') ?? '';
    const port = get('port');
    const returnTo = get('return');
    const isMcp = returnTo === MCP_RETURN;
    const rawName = isMcp ? '' : (get('name') ?? '');
    const name = sanitizeName(rawName);

    const hasValidReturn = returnTo === null || isMcp;
    const hasValidChallenge = CODE_CHALLENGE_PATTERN.test(codeChallenge);
    const hasValidPort = port === null || (!isMcp && isValidCliCallbackPort(port));
    const sendsCodeBack = isMcp || port !== null;
    const hasStateWhenNeeded = !sendsCodeBack || state !== '';
    const hasNameWhenNeeded = isMcp || name !== '';
    if (!hasNameWhenNeeded || !hasValidReturn || !hasValidChallenge || !hasValidPort || !hasStateWhenNeeded) {
      return null;
    }

    return new CliAuthorizationRequest({ name, codeChallenge, state, port, isMcp });
  }

  withName(name: string): CliAuthorizationRequest {
    const safeName = sanitizeName(name);
    return new CliAuthorizationRequest({ ...this, name: safeName });
  }
}

function sanitizeName(rawName: string): string {
  const cleanedName = rawName.replace(UNSAFE_NAME_CHARACTERS, '').trim();
  // By code point: a UTF-16 slice could split a surrogate pair, which the
  // backend cannot store.
  const codePoints = Array.from(cleanedName);
  return codePoints.slice(0, MAX_NAME_LENGTH).join('').trim();
}

export interface CliAuthorizationRequestData {
  name: string;
  codeChallenge: string;
  state: string;
  port: string | null;
  isMcp: boolean;
}
